// @ts-strict-ignore
import { NumberUtils } from '@shared/util/number-utils';
import { UserProfileVO, UserVO } from '@user/model/vo/user.vo';
import { ArticleCollectionVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleCollectionUtils } from '@article/util/article-collection-utils';
import { ArrayUtils } from '@shared/util/array-utils';

export class UserProfileUtils {
  public static mergeArticleCollectionsInUserProfiles(source: UserProfileVO, target: UserProfileVO): boolean {
    if (!source || !target) {
      return false;
    }
    const sourceArticleCollections: ArticleCollectionVO[] = source.articleCollections;
    if (!sourceArticleCollections) {
      return false;
    }
    let targetArticleCollections: ArticleCollectionVO[] = target.articleCollections;
    if (!targetArticleCollections) {
      targetArticleCollections = new Array<ArticleCollectionVO>();
    } else {
      targetArticleCollections = targetArticleCollections.concat();
    }
    let targetArticleCollectionsModified: boolean = false;
    for (let i: number = 0; i < sourceArticleCollections.length; i++) {
      const sourceArticleCollection: ArticleCollectionVO = sourceArticleCollections[i];
      if (!sourceArticleCollection) {
        continue;
      }
      if (sourceArticleCollection.type === ArticleCollectionVO.TYPE_NORMAL) {
        if (!NumberUtils.isNumber(sourceArticleCollection.userId) || sourceArticleCollection.userId === target.user.id) {
          targetArticleCollections.push(sourceArticleCollection);
          targetArticleCollectionsModified = true;
        } else if (sourceArticleCollection.userId === UserVO.DEFAULT_GUEST_ACCOUNT_ID) {
          let targetArticleCollection: ArticleCollectionVO = ArticleCollectionUtils.getArticleCollectionByTypeAndNameAndUserId(
            targetArticleCollections,
            sourceArticleCollection.type,
            sourceArticleCollection.name,
            target.user.id
          );
          if (!targetArticleCollection) {
            targetArticleCollection = ArticleCollectionUtils.createArticleCollection(sourceArticleCollection.type, null, target.user.id);
            targetArticleCollection.name = sourceArticleCollection.name;
            targetArticleCollections.push(targetArticleCollection);
            targetArticleCollectionsModified = true;
          }
          targetArticleCollectionsModified =
            ArticleCollectionUtils.mergeArticleCollections(sourceArticleCollection, targetArticleCollection) || targetArticleCollectionsModified;
        }
      } else if (sourceArticleCollection.type === ArticleCollectionVO.TYPE_FAVORITES) {
        let targetArticleCollection: ArticleCollectionVO = ArticleCollectionUtils.getArticleCollectionByType(
          targetArticleCollections,
          sourceArticleCollection.type
        );
        if (!targetArticleCollection) {
          targetArticleCollection = ArticleCollectionUtils.createArticleCollection(sourceArticleCollection.type, null, target.user.id);
          targetArticleCollections.push(targetArticleCollection);
          targetArticleCollectionsModified = true;
        }
        targetArticleCollectionsModified =
          ArticleCollectionUtils.mergeArticleCollections(sourceArticleCollection, targetArticleCollection) || targetArticleCollectionsModified;
      }
    }
    if (targetArticleCollectionsModified) {
      target.articleCollections = targetArticleCollections;
    }
    return targetArticleCollectionsModified;
  }

  public static secureExistenceOfFavoritesArticleCollection(userProfile: UserProfileVO): void {
    if (!userProfile) {
      return;
    }
    let articleCollections: ArticleCollectionVO[] = userProfile.articleCollections;
    if (!articleCollections) {
      articleCollections = new Array<ArticleCollectionVO>();
    }
    for (let i: number = 0; i < articleCollections.length; i++) {
      const articleCollection: ArticleCollectionVO = articleCollections[i];
      if (articleCollection && articleCollection.type === ArticleCollectionVO.TYPE_FAVORITES) {
        return;
      }
    }
    userProfile.articleCollections = articleCollections.concat(
      ArticleCollectionUtils.createArticleCollection(ArticleCollectionVO.TYPE_FAVORITES, null, userProfile.user.id)
    );
  }

  public static addFavoriteArticlesToProfile(userProfile: UserProfileVO, articleIds: number[]): void {
    if (!userProfile || ArrayUtils.isEmpty(articleIds)) {
      return;
    }
    const favoriteArticleCollection: ArticleCollectionVO = this.getFavoritesArticleCollection(userProfile);
    if (!favoriteArticleCollection) {
      return;
    }
    ArticleCollectionUtils.mergeArticleCollections(
      ArticleCollectionUtils.createArticleCollection(ArticleCollectionVO.TYPE_FAVORITES, articleIds),
      favoriteArticleCollection
    );
  }

  public static getFavoritesArticleCollection(userProfile: UserProfileVO): ArticleCollectionVO {
    if (!userProfile) {
      return null;
    }
    for (const articleCollection of userProfile.articleCollections) {
      if (articleCollection && articleCollection.type === ArticleCollectionVO.TYPE_FAVORITES) {
        return articleCollection;
      }
    }
    return null;
  }

  public static resetUserProfile(userProfile: UserProfileVO): void {
    if (!userProfile) {
      return;
    }
    userProfile.articleCollections = [];
  }

  public static getAvailableArticleCollectionsForAddingArticleByUser(userProfile: UserProfileVO, article: ArticleVO): ArticleCollectionVO[] {
    if (!article) {
      return [];
    }
    return this.getAvailableArticleCollectionsForAddingArticleByUserAndId(userProfile, article.identity);
  }

  public static getAllAvailableArticleCollections(userProfile: UserProfileVO, articles: ArticleVO[]): ArticleCollectionVO[] {
    if (!articles) {
      return [];
    }
    const articleCollections: ArticleCollectionVO[] = userProfile.articleCollections;
    if (ArrayUtils.isEmpty(articleCollections)) {
      return [];
    }
    return articleCollections;
  }

  public static getAvailableArticleCollectionsForAddingArticleByUserAndId(userProfile: UserProfileVO, articleId: number): ArticleCollectionVO[] {
    if (!userProfile || !articleId) {
      return [];
    }
    const articleCollections: ArticleCollectionVO[] = userProfile.articleCollections;
    if (ArrayUtils.isEmpty(articleCollections)) {
      return [];
    }
    const result: ArticleCollectionVO[] = [];
    for (let i = 0; i < articleCollections.length; i++) {
      const articleCollection: ArticleCollectionVO = articleCollections[i];
      if (!articleCollection) {
        continue;
      }
      if (ArrayUtils.isNotEmpty(articleCollection.articleIds) && articleCollection.articleIds.indexOf(articleId) !== -1) {
        continue;
      }
      result.push(articleCollection);
    }
    return result;
  }

  public static isArticleInAnyCollection(userProfile: UserProfileVO, articleid: number): boolean {
    return userProfile.articleCollections.find((collection) => collection.articleIds.find((articleId) => articleId === articleid)) !== undefined;
  }
}
